import { ExternalSiteIcon } from '@/components/icons/externalsite-icon';
import { AnalyticsPageCategories } from '@/enums/analytics-type';
import { dataTestTransformer } from '@/utils/data-test-helper';
import { RichTextLinkNode } from '@cinemark/content-api-client';
import type { SerializedLinkNode, SerializedTextNode } from '@payloadcms/richtext-lexical';
import { VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { CinemarkLink } from '../cinemark-link/cinemark-link';
import { Anchor, anchorVariants } from '../ui/anchor';
import { NodesToJSX } from './rich-text-renderer';

type RichTextLinkConverterComponentProps = {
  node: SerializedLinkNode | undefined;
  nodesToJSX: NodesToJSX;
};

export const RichTextLinkConverterComponent = ({ node, nodesToJSX }: RichTextLinkConverterComponentProps): React.JSX.Element | null => {
  if (!node || node.type !== 'link' || !node.fields || typeof node.fields !== 'object' || !node.children?.length) return null;

  const text = node.children[0] as SerializedTextNode;
  const children = nodesToJSX({ nodes: node.children ?? [] });
  const fields = node.fields as unknown as RichTextLinkNode;
  const dataTest = `link-${dataTestTransformer(text.text)}`;

  const getAlignmentStyle = (alignment: string | undefined): string => {
    switch (alignment) {
      case 'left': return 'self-start';
      case 'center': return 'self-center';
      case 'right': return 'self-end';
      default: return '';
    }
  };

  const getSizeStyle = (variant: string | undefined): VariantProps<typeof anchorVariants>['size'] => {
    switch (variant) {
      case 'sm': return 'sm';
      case 'lg': return 'lg';
      case 'icon': return 'icon';
      default: return 'default';
    }
  };

  const getVariantStyle = (variant: string | undefined): VariantProps<typeof anchorVariants>['variant'] => {
    switch (variant) {
      case 'secondary': return 'outline';
      default: return 'default';
    }
  };

  if (fields.asButton) {
    return (
      <Anchor className={getAlignmentStyle(fields.alignment)}
        data-test={dataTest}
        href={node.fields.url || '/'}
        size={getSizeStyle(fields.size)}
        target={node.fields.newTab ? '_blank' : undefined}
        variant={getVariantStyle(fields.variant)}
      >
        {children}
        {fields.isExternal &&
        <ExternalSiteIcon aria-label={`${text.text} external link icon`}
          aria-labelledby={`${text.text}IconTitle`}
          className="icon-external-link icon-primary inline ml-xs"
          role="img"
        />}
      </Anchor>
    );
  }

  return (
    <CinemarkLink useAnchor
      className={clsx('text-primary hover:text-primary-darker group font-bold', getAlignmentStyle(fields.alignment))}
      dataLinkCategory={AnalyticsPageCategories.CONTENT_PAGE}
      dataLinkName={text.text}
      dataTest={dataTest}
      href={node.fields.url || '/'}
      target={node.fields.newTab ? '_blank' : undefined}
    >
      <span>{children}</span>
      {fields.isExternal &&
        <ExternalSiteIcon aria-label={`${text.text} external link icon`}
          aria-labelledby={`${text.text}IconTitle`}
          className="icon-external-link icon-primary flex-shrink-0 group-hover:!fill-primary-darker"
          role="img"
        />
      }
    </CinemarkLink>
  );
};
